import React, { useState, useEffect } from 'react';
import { Mail, Phone, User, Calendar, AlertCircle, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { adminAPI, handleApiError } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmDialog from '../components/ConfirmDialog';
import { useFlashMessage } from '../components/FlashMessage';

const ContactMessages = () => {
  const { isSystemAdmin } = useAuth();
  const { showSuccess, showDanger, FlashContainer } = useFlashMessage();
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await adminAPI.getContactMessages();
      if (response.data.success) {
        setMessages(response.data.data.messages);
      }
    } catch (err) {
      console.error('Failed to load contact messages:', err);
      setError(handleApiError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (message) => {
    setMessageToDelete(message);
    setDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      const response = await adminAPI.deleteContactMessage(messageToDelete.id);
      if (response.data.success) {
        showSuccess(`"${messageToDelete.name}" mesajı başarıyla silindi.`);
        setDialogOpen(false);
        setMessageToDelete(null);
        loadMessages();
      } else {
        showDanger(response.data.message || 'Mesaj silinirken hata oluştu');
      }
    } catch (err) {
      console.error('Failed to delete message:', err);
      showDanger(`Mesaj silinirken hata oluştu: ${handleApiError(err)}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!isSystemAdmin()) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
          <p className="text-red-700">Bu sayfaya erişim yetkiniz yok.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">İletişim Mesajları</h1>
        <p className="text-gray-600">Web sitesinden gelen iletişim formları</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            Mesajlar ({messages.length})
          </h3>
        </div>
        <div className="card-body">
          {messages.length > 0 ? (
            <div className="space-y-4">
              {messages.map((msg) => (
                <div key={msg.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span className="font-medium text-gray-900">{msg.name}</span>
                      </div>
                      {msg.email && (
                        <div className="flex items-center gap-2 mb-1">
                          <Mail className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-600">{msg.email}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mb-2">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">{msg.phone}</span>
                      </div>
                      {msg.subject && (
                        <div className="mb-2">
                          <span className="text-sm font-medium text-gray-700">Konu: </span>
                          <span className="text-sm text-gray-600">{msg.subject}</span>
                        </div>
                      )}
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{msg.message}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Calendar className="w-4 h-4" />
                      {new Date(msg.created_at).toLocaleString('tr-TR')}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button
                      onClick={() => handleDeleteClick(msg)}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Sil
                    </button>
                  </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Henüz mesaj bulunmuyor</p>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Mesajı Sil"
        message={`${messageToDelete?.name} mesajını silmek istediğinizden emin misiniz?`}
        confirmText="Sil"
        cancelText="İptal"
        type="danger"
        loading={deleteLoading}
      />

      <FlashContainer />
    </div>
  );
};

export default ContactMessages;
